import { supabase } from './supabaseClient'
import { getUserFriendlyMessage, getUserFriendlyMessageFromText } from './errors'
import { captureError } from './sentry'

type OAuthStartResponse = {
  ok?: boolean
  auth_url?: string
  error?: string
}

type SyncCalendarResponse = {
  ok?: boolean
  synced?: number
  exported?: number
  conflicts?: number
  source?: string
  error?: string
}

type DetectConflictsResponse = {
  ok?: boolean
  conflicts?: number
  resolved?: number
  error?: string
}

export type CalendarSyncResult = {
  ok: boolean
  synced?: number
  exported?: number
  conflicts?: number
  error?: string
}

export async function startGoogleCalendarOAuth(): Promise<{ ok: boolean; error?: string }> {
  const { data, error } = await supabase.functions.invoke<OAuthStartResponse>('google-calendar-oauth', {
    body: {},
  })

  if (error) {
    captureError(error, { function: 'google-calendar-oauth' })
    return { ok: false, error: await getUserFriendlyMessage(error) }
  }
  if (!data?.ok || !data.auth_url) {
    return { ok: false, error: getUserFriendlyMessageFromText(data?.error ?? 'Unable to start Google sign-in') }
  }

  window.location.href = data.auth_url
  return { ok: true }
}

export async function syncGoogleCalendar(): Promise<CalendarSyncResult> {
  const { data, error } = await supabase.functions.invoke<SyncCalendarResponse>('sync-calendar', {
    body: { provider: 'google' },
  })

  const responseError = data?.error
  if (responseError) return { ok: false, error: getUserFriendlyMessageFromText(responseError) }
  if (error) {
    captureError(error, { function: 'sync-calendar' })
    return { ok: false, error: await getUserFriendlyMessage(error) }
  }
  if (!data?.ok) return { ok: false, error: getUserFriendlyMessageFromText('Calendar sync failed') }

  return {
    ok: true,
    synced: data.synced,
    exported: data.exported,
    conflicts: data.conflicts,
  }
}

export async function detectCalendarConflicts(): Promise<{
  ok: boolean
  conflicts?: number
  resolved?: number
  error?: string
}> {
  const { data, error } = await supabase.functions.invoke<DetectConflictsResponse>(
    'detect-calendar-conflicts',
    { body: {} },
  )

  const responseError = data?.error
  if (responseError) return { ok: false, error: getUserFriendlyMessageFromText(responseError) }
  if (error) {
    captureError(error, { function: 'detect-calendar-conflicts' })
    return { ok: false, error: await getUserFriendlyMessage(error) }
  }
  if (!data?.ok) return { ok: false, error: getUserFriendlyMessageFromText('Conflict detection failed') }

  return { ok: true, conflicts: data.conflicts, resolved: data.resolved }
}

/** Sync Google Calendar, then always run conflict detection. */
export async function syncCalendarWithConflictCheck(): Promise<CalendarSyncResult> {
  const syncResult = await syncGoogleCalendar()
  if (!syncResult.ok) return syncResult

  const detectResult = await detectCalendarConflicts()
  if (!detectResult.ok) {
    return {
      ok: true,
      synced: syncResult.synced,
      exported: syncResult.exported,
      conflicts: syncResult.conflicts,
      error: detectResult.error,
    }
  }

  return {
    ok: true,
    synced: syncResult.synced,
    exported: syncResult.exported,
    conflicts: Math.max(detectResult.conflicts ?? 0, syncResult.conflicts ?? 0),
  }
}

export async function runDemoCalendarSync(
  events: Record<string, unknown>[],
): Promise<{ ok: boolean; synced?: number; error?: string }> {
  const { data, error } = await supabase.functions.invoke<SyncCalendarResponse>('sync-calendar', {
    body: { provider: 'google', source: 'demo', events },
  })

  const responseError = data?.error
  if (responseError) return { ok: false, error: getUserFriendlyMessageFromText(responseError) }
  if (error) {
    captureError(error, { function: 'sync-calendar', context: 'demo' })
    return { ok: false, error: await getUserFriendlyMessage(error) }
  }
  if (!data?.ok) return { ok: false, error: getUserFriendlyMessageFromText('Demo sync failed') }

  return { ok: true, synced: data.synced }
}
